/**
 * @fileoverview ARITA Master Precedent Library (admin)
 *
 * URL: /admin/master-library
 * Protection: Requires super_admin (gated by the parent /admin/_layout route).
 *
 * Renders the exact same precedent editor firms use ({@link DocsSettingsInner})
 * pointed at the singleton ARITA master org. Future improvements to the firm
 * precedent tooling automatically apply here too.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/ui/primitives";
import type { Id } from "@cvx/_generated/dataModel";
import { DocsSettingsInner } from "../dashboard/_layout.settings.docs";

export const Route = createFileRoute(
  "/_app/_auth/admin/_layout/master-library",
)({
  component: MasterLibraryPage,
});

function MasterLibraryPage() {
  const [masterOrgId, setMasterOrgId] = useState<Id<"organizations"> | null>(
    null,
  );

  const { mutateAsync: ensureMasterOrg } = useMutation({
    mutationFn: useConvexMutation(api.admin.masterLibrary.ensureMasterOrg),
  });

  // Create the master org if needed and ensure this super admin is a member
  // (so the shared, membership-gated precedent queries load). Idempotent.
  const ensuredRef = useRef(false);
  useEffect(() => {
    if (ensuredRef.current) return;
    ensuredRef.current = true;
    ensureMasterOrg({})
      .then((id) => setMasterOrgId(id))
      .catch(() => {
        ensuredRef.current = false;
      });
  }, [ensureMasterOrg]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">ARITA Master Library</h1>
        <p className="text-sm text-muted-foreground">
          Create and configure shared master precedents. Firms granted access
          can add these to their own library.
        </p>
      </div>

      {!masterOrgId ? (
        <div className="flex w-full items-center justify-center py-20">
          <Spinner className="size-6" />
        </div>
      ) : (
        <DocsSettingsInner orgId={masterOrgId} canEdit mode="master" />
      )}
    </div>
  );
}
